/**
 * @fileoverview Axios HTTP Client Configuration with JWT Interceptors
 *
 * This module configures the Axios HTTP client for the webchat application,
 * providing automatic JWT token management and error handling.
 *
 * **Key Features:**
 * - Response interceptor for JWT error handling (403, expired, invalid signature)
 * - Automatic token refresh on authentication errors
 * - Support for function-based getToken() or URL-based token endpoint
 * - Retry mechanism (max 5 retries) for transient failures
 * - iOS SDK deep link notification for JWT expiry
 * - Sentry error logging for debugging
 *
 * **Token Refresh Flow:**
 * 1. Request fails with 403/JWT error
 * 2. Check if getToken is configured (function or URL)
 * 3. Fetch new token using configured method
 * 4. Update Authorization header
 * 5. Retry original request with new token
 *
 * @module helpers/axios
 * @author KeyReply Engineering Team
 *
 * @techdebt
 * - Module-level `retries` variable is not thread-safe
 * - Retry logic doesn't use exponential backoff
 * - Error messages are hardcoded - should be configurable
 * - iOS SDK notification uses URL scheme - may not work in all contexts
 *
 * @security
 * - JWT tokens are stored in Authorization header
 * - Token refresh prevents credential exposure in logs
 *
 * @example Token Configuration Options
 * ```typescript
 * // Option 1: Function-based token retrieval
 * window.kr_settings = {
 *   getToken: async () => {
 *     const response = await fetch('/api/token');
 *     return response.json().then(data => data.token);
 *   }
 * }
 *
 * // Option 2: URL-based token retrieval
 * window.kr_settings = {
 *   getToken: {
 *     url: '/api/token',
 *     method: 'POST',
 *     accessTokenPath: 'data.access_token'
 *   }
 * }
 * ```
 */

import axios, { Method } from "axios";
import * as _ from "lodash-es";
import { useMainStore } from "../stores/main";
import * as Sentry from "@sentry/browser";

/**
 * Counter for retry attempts to prevent infinite loops
 * @techdebt Module-level state is not ideal for concurrent requests
 */
let retries = 0;

/**
 * Regular expression to validate JWT token format
 * Matches standard JWT structure: header.payload.signature
 */
const isJWTRegex = /^[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*$/;

/**
 * Log Axios errors to Sentry for monitoring and debugging
 *
 * Handles three types of errors:
 * 1. Response errors (server returned error status)
 * 2. Request errors (request made but no response received)
 * 3. Setup errors (error occurred while setting up request)
 *
 * @param {any} error - Axios error object
 * @techdebt Error parameter should be properly typed as AxiosError
 */
const logAxiosExceptionToSentry = (error: any) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    Sentry.captureException(
      new Error(
        JSON.stringify({
          response: error?.response?.data,
          status: error?.response?.status,
          url: (error?.response?.config && error?.response?.config?.url) || "",
        })
      )
    );
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser
    // - see: https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest
    // and an instance of http.ClientRequest in node.js
    Sentry.captureException(
      new Error(
        JSON.stringify({
          response: error?.request?.responseText || "",
          status: error?.request?.status,
          url: error?.request?.responseURL || "",
        })
      )
    );
  } else {
    // Something happened in setting up the request that triggered an Error
    Sentry.captureException(new Error(error.message));
  }
};

/**
 * Display JWT error message to user via drawer notification
 *
 * Shows a modal/drawer with the error message and sets up a reload action
 * when the user acknowledges the error.
 *
 * @param {string} [errorMessage] - Custom error message to display
 * @default "Your generated token method is invalid. Please check the method or secret."
 */
const showJWTErrorMessage = (errorMessage?: string) => {
  const store = useMainStore();
  store.setNativeAction(() => {
    window.top?.location.reload();
  });
  store.jwtMessage({
    title: "Session Error",
    text:
      errorMessage || "Your generated token method is invalid. Please check the method or secret.",
  });
};

axios.interceptors.response.use(
  async (response) => {
    return response;
  },
  async (error) => {
    const getTokenUrl = _.get(window, "kr_settings.getToken.url");
    const getTokenMethod = _.get(window, "kr_settings.getToken.method");
    const accessTokenPath = _.get(window, "kr_settings.getToken.accessTokenPath");
    const getToken = _.get(window, "kr_settings.getToken");
    const iosSDK = _.get(window, "kr_settings.appTokenConfigured");

    const forbidden = _.get(error, "response.status") === 403;
    const responseData = _.get(error, "response.data", {});
    const invalidSignature = responseData?.name === "JsonWebTokenError";
    const jwtExpired = responseData?.name === "TokenExpiredError";

    const jwtGeneralError = forbidden || invalidSignature || jwtExpired;

    if (!jwtGeneralError) {
      logAxiosExceptionToSentry(error);
      return Promise.reject(error);
    }

    // For auto generation of JWT + auto retry (AIASG)
    // Website
    if (typeof getToken === "function" || isTokenConfigured(getTokenUrl, getTokenMethod)) {
      if (retries > 5) {
        retries = 0;
        showJWTErrorMessage();
        return Promise.reject(error);
      }
      let jwt = "";
      // Refresh new token
      if (typeof getToken === "function") {
        jwt = await getToken().catch((error: any) => {
          // Show the specific error message from the server
          showJWTErrorMessage(error.message);
          return Promise.reject(error);
        });
      } else if (getTokenUrl && getTokenMethod) {
        jwt = await getNewToken(getTokenUrl, getTokenMethod, accessTokenPath || '').catch((error) => {
          // Show the specific error message from the server
          showJWTErrorMessage(error.message);
          return Promise.reject(error);
        });
      }
      if (isJWTRegex.test(jwt)) {
        setTokenToAxiosAuthorizationHeader(jwt);
        const response = await recallErrorRequest(error, jwt);
        return response;
      } else {
        showJWTErrorMessage();
        return Promise.reject(error);
      }
    }
    // IPOS
    else if (iosSDK) {
      window.location.href = "keyreplysdk://?alert=jwtexpired";
      return {
        data: {
          jwtExpiredAppFlow: true,
          requestData: _.pick(error.response.config, ["method", "data", "url", "headers"]),
        },
      };
    }
    // For notifying JWT error when JWT is manually generated from external (Panin)
    else {
      const store = useMainStore();
      if (invalidSignature) {
        store.jwtMessage({
          title: "Invalid Signature",
          text: "Your session has invalid signature. Please re-enter the valid one.",
        });
      } else if (jwtExpired) {
        store.jwtMessage({
          title: "Session Expired",
          text: "Your session has been expired. Please re-enter the new session.",
        });
      } else {
        store.jwtMessage({
          title: "Invalid",
          text: "Your session is invalid. Please re-enter the valid one.",
        });
      }
    }
  }
);

/**
 * Check if token configuration is properly set up
 *
 * Validates that both URL and HTTP method are provided for
 * URL-based token retrieval configuration.
 *
 * @param {string} url - Token endpoint URL
 * @param {Method} method - HTTP method (GET, POST, etc.)
 * @returns {boolean} True if configuration is valid
 */
export const isTokenConfigured = (url?: string, method?: string) => {
  return !!(url && method);
};

/**
 * Fetch a new JWT token from the configured endpoint
 *
 * Makes a request to the token URL without Authorization header
 * to get a fresh token. Handles common error responses.
 *
 * @param {string} url - Token endpoint URL
 * @param {Method} method - HTTP method for the request
 * @param {string} accessTokenPath - Lodash path to extract token from response
 *                                   (e.g., "data.access_token")
 * @returns {Promise<string>} The new JWT token
 * @throws {Error} If token retrieval fails with specific error messages
 *
 * @example
 * const token = await getNewToken(
 *   '/api/refresh-token',
 *   'POST',
 *   'data.token'
 * );
 */
export const getNewToken = async (url: string, method: Method, accessTokenPath: string) => {
  // Create a new axios instance without Authorization header
  // to avoid circular dependency issues
  const axiosAuthInstance = axios.create();
  delete axiosAuthInstance.defaults.headers["Authorization"];
  try {
    const response = await axiosAuthInstance({
      url,
      method,
    });

    // Extract token using configured path if provided
    if (accessTokenPath) {
      return _.get(response, accessTokenPath);
    }

    return response.data;
  } catch (error: any) {
    // Handle specific error messages from server
    if (error.response) {
      if (error.response.status === 403) {
        throw new Error("Token expired");
      } else if (error.response.status === 401) {
        throw new Error("Invalid token");
      } else if (error.response.status === 500 && error.response.data?.error) {
        throw new Error(error.response.data.error);
      }
    }
    throw error; // Re-throw if not a specific error we want to handle
  }
};

/**
 * Retry a failed request with a new JWT token
 *
 * Updates the Authorization header with the new token and
 * re-executes the original failed request.
 *
 * @param {any} error - Original Axios error containing request config
 * @param {string} token - New JWT token to use
 * @returns {Promise<AxiosResponse>} Response from retried request
 *
 * @techdebt
 * - error parameter should be typed as AxiosError
 * - Modifies error object in place (impure function)
 * - Increments module-level retries counter
 */
export const recallErrorRequest = async (error: any, token: string) => {
  retries++;
  Object.assign(error.response.config.headers, {
    Authorization: `Bearer ${token}`,
  });
  const { data, url, headers, method } = error.response.config;
  const response = await axios({
    method,
    data,
    url,
    headers,
  });

  return response;
};

/**
 * Set JWT token in Axios default Authorization header
 *
 * Updates the global Axios defaults so all subsequent requests
 * automatically include the Bearer token.
 *
 * @param {string} jwt - JWT token to set
 *
 * @security Token is stored in memory only, not persisted
 */
export const setTokenToAxiosAuthorizationHeader = async (jwt: string) => {
  axios.defaults.headers["Authorization"] = `Bearer ${jwt}`;
};

/**
 * Configured Axios instance with JWT interceptors
 * Use this for all API requests to get automatic token handling
 */
export default axios;
